<script setup lang="ts">
import { NButton } from 'naive-ui'
import { useFormattedDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import type { CvGenerationRun } from '../stores/cv_generation_runs'

// The Generate button with what it is doing: the run in flight, if any,
// and the last few failed runs with their reasons. The parent owns the
// store; this panel only reports the click.
defineProps<{
  active: CvGenerationRun | null
  failed: CvGenerationRun[]
  triggering: boolean
}>()

const emit = defineEmits<{ generate: [] }>()

const { formatDate, formatAbsoluteDate } = useFormattedDate()
const now = useNow()

function since(run: CvGenerationRun): string {
  const at = run.started_at ?? run.created_at
  return `${run.status === 'running' ? 'running' : 'queued'} since ${formatDate(at, now.value)}`
}
</script>

<template>
  <div class="flex flex-col gap-2" data-testid="cv-generation-panel">
    <div class="flex items-center justify-between gap-3">
      <h2 class="text-lg font-semibold text-white m-0">Versions</h2>
      <NButton
        type="primary"
        size="small"
        :loading="triggering || active !== null"
        :disabled="active !== null"
        data-testid="cv-generate"
        @click="emit('generate')"
      >
        {{ active ? 'Generating…' : 'Generate draft' }}
      </NButton>
    </div>
    <p class="text-xs text-white/50 m-0">
      Queues one call to the model over About Me, your entity page, and the pages it links to. Takes about a minute; the
      draft appears here when it lands.
    </p>
    <p v-if="active" class="text-xs text-amber-300/90 m-0" data-testid="cv-run-active">
      Run {{ active.id }} {{ since(active) }}
    </p>
    <ul v-if="failed.length" class="list-none p-0 m-0 flex flex-col gap-1" data-testid="cv-runs-failed">
      <li
        v-for="run in failed"
        :key="run.id"
        class="text-xs text-red-300/90"
        :title="run.finished_at ? formatAbsoluteDate(run.finished_at) : undefined"
        :data-testid="`cv-run-failed-${run.id}`"
      >
        Run {{ run.id }} failed<span v-if="run.finished_at"> {{ formatDate(run.finished_at, now) }}</span
        >: {{ run.error }}
      </li>
    </ul>
  </div>
</template>
